import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import {
	IBellNotification,
	IBellNotifications,
	INotification,
} from '@app/services/notifications-bell/models/notifcations-bell-models';
import { NotificationsBellApiService } from '@app/services/notifications-bell/api/notifications-bell-api.service';

@Injectable({
	providedIn: 'root',
})
export class NotificationsBellService {
	private _pollingInterval = 60000;
	private _updating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _notifications$: BehaviorSubject<IBellNotification[]> = new BehaviorSubject<IBellNotification[]>([]);
	private _oldNotifications$: BehaviorSubject<INotification[]> = new BehaviorSubject<INotification[]>([]);

	constructor(private auth: AuthService, private notificationsBellApiService: NotificationsBellApiService) {}

	public isUpdating$(): Observable<boolean> {
		return this._updating$.asObservable();
	}

	public setUpdating(isUpdating: boolean): void {
		this._updating$.next(isUpdating);
	}

	public fetchBellNotifications(): Observable<IBellNotifications> {
		return timer(0, this._pollingInterval).pipe(
			switchMap((_): Observable<IBellNotifications> => {
				return this.notificationsBellApiService.fetchBellNotifications().pipe(
					tap((bellNotifications) => {
						this._notifications$.next(bellNotifications.notifications);
					}),
				);
			}),
		);
	}

	public getNotifications(): Observable<IBellNotification[]> {
		return this._notifications$.asObservable();
	}

	public setNotifications(notification: IBellNotification[]) {
		this._notifications$.next(notification);
	}

	public getOldNotifications(): Observable<INotification[]> {
		return this._oldNotifications$.asObservable();
	}

	public dismissNotification(notifications: IBellNotification[]): Observable<{}> {
		const singleNotification = notifications.length === 1;
		if (!singleNotification) {
			this.setUpdating(true);
		}

		const payload = {
			messages: notifications.map(({ date, org_id }) => ({ date, org_id })),
		};

		return this.notificationsBellApiService.dismissNotification(payload).pipe(
			tap(() => {
				if (singleNotification) {
					const { date, org_id } = notifications[0];
					const filteredNotReadNotifications = this._notifications$.value.reduce((acc, item: IBellNotification) => {
						if (item.date === date && item.org_id === org_id) {
							return acc;
						}
						return [...acc, item];
					}, []);
					this._notifications$.next(filteredNotReadNotifications);
				} else {
					this._notifications$.next([]);
				}
				this.setUpdating(false);
			}),
		);
	}

	push(alert: INotification): void {
		this._oldNotifications$.next([...this._oldNotifications$.value, alert]);
	}

	delete(alert: INotification): void {
		this._oldNotifications$.next([...this._oldNotifications$.value.filter((a) => a !== alert)]);
	}

	public preset(): void {
		this._oldNotifications$.next([]);
	}
}
